import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Calendar, Award, Crown, Building2, Gift } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useMemberAuth, memberApi } from "../lib/memberAuth";
import Logo from "../components/Logo";
import { toast } from "sonner";

const TIER_META = {
  founders: { label: "Founders 100", Icon: Crown, gold: true },
  executive: { label: "Executive", Icon: Award, gold: false },
  corporate: { label: "Corporate", Icon: Building2, gold: false },
};

export function MembershipSection() {
  const { member } = useMemberAuth();
  const [card, setCard] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [visits, setVisits] = useState([]);
  const [points, setPoints] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);

  const loadCard = useCallback(async () => {
    try {
      const r = await memberApi.get("/member/card-token");
      setCard(r.data);
      const exp = new Date(r.data.expires_at).getTime();
      setCountdown(Math.max(0, Math.floor((exp - Date.now()) / 1000)));
    } catch (e) { /* ignore */ }
  }, []);

  const loadVisits = useCallback(async () => {
    try {
      const r = await memberApi.get("/member/visits");
      setVisits(r.data);
    } catch (e) { /* ignore */ }
  }, []);

  const loadLoyalty = useCallback(async () => {
    try {
      const [p, r, d] = await Promise.all([
        memberApi.get("/member/points"),
        memberApi.get("/rewards"),
        memberApi.get("/member/redemptions"),
      ]);
      setPoints(p.data); setRewards(r.data); setRedemptions(d.data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { if (member) { loadCard(); loadVisits(); loadLoyalty(); } }, [member, loadCard, loadVisits, loadLoyalty]);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  useEffect(() => { if (countdown === 0 && card) loadCard(); }, [countdown, card, loadCard]);

  if (!member) return null;
  const meta = TIER_META[member.membership_type] || TIER_META.executive;
  const Icon = meta.Icon;

  return (
    <div data-testid="member-dashboard">
      <div className="grid lg:grid-cols-12 gap-10">
        <section className="lg:col-span-7" data-testid="member-card-section">
          <p className="eyebrow">Your Digital Membership</p>
          <h2 className="font-serif text-3xl sm:text-4xl mt-3 leading-tight">Present this at the door.</h2>

          <div className="mt-8 relative overflow-hidden" data-testid="member-card">
            <div className={`p-8 sm:p-10 ${meta.gold ? "bg-gradient-to-br from-[#1f1a14] to-[#0c0a08]" : "bg-[var(--twr-surface)]"} border ${meta.gold ? "border-[var(--twr-gold)]/40" : "border-[var(--twr-line-strong)]"}`}>
              <div className="flex items-start justify-between gap-6">
                <div>
                  <div className="flex items-center gap-2 text-[var(--twr-bronze)]">
                    <Icon size={16} strokeWidth={1.4} />
                    <p className="eyebrow">{meta.label}</p>
                  </div>
                  <p className="font-serif text-3xl sm:text-4xl mt-6 leading-tight">{member.full_name}</p>
                  <p className="font-serif text-lg text-[var(--twr-bronze)] mt-2">{member.member_no}</p>
                </div>
                <Logo variant="icon" />
              </div>

              <div className="mt-10 flex items-end justify-between gap-6 flex-wrap">
                <div className="bg-white p-3 inline-block" data-testid="member-qr">
                  {card?.token ? (
                    <QRCodeSVG key={card.token} value={card.token} size={150} level="M" includeMargin={false} bgColor="#FFFFFF" fgColor="#0A0A0A" />
                  ) : (
                    <div className="w-[150px] h-[150px] bg-zinc-200 flex items-center justify-center text-zinc-500 text-xs">Loading…</div>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-[11px] tracking-[0.28em] uppercase text-[var(--twr-warm-white)]/55">Code refreshes in</p>
                  <p className="font-serif text-3xl text-[var(--twr-bronze)] mt-1" data-testid="member-card-countdown">
                    {Math.floor(countdown / 60).toString().padStart(2, "0")}:{(countdown % 60).toString().padStart(2, "0")}
                  </p>
                  <button onClick={loadCard} data-testid="member-card-refresh" className="mt-3 inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[var(--twr-warm-white)]/65 hover:text-[var(--twr-bronze)] transition-colors">
                    <RefreshCw size={12} /> Refresh
                  </button>
                </div>
              </div>
            </div>
          </div>

          <p className="text-[var(--twr-warm-white)]/45 text-[12px] mt-4">
            For your security, the QR code rotates every 60 minutes. Keep your phone close.
          </p>
        </section>

        <aside className="lg:col-span-5 space-y-6">
          <div className="surface p-7" data-testid="member-summary">
            <p className="eyebrow">At a glance</p>
            <div className="mt-5 space-y-3 text-sm">
              <Row label="Member No." value={member.member_no} />
              <Row label="Tier" value={meta.label} />
              <Row label="Email" value={member.email} />
              {member.company && <Row label="Company" value={member.company} />}
              <Row label="Inducted" value={new Date(member.created_at).toLocaleDateString("en-US", { month: "long", day: "2-digit", year: "numeric" })} />
            </div>
          </div>

          <div className="surface p-7" data-testid="member-points-card">
            <div className="flex items-center justify-between">
              <p className="eyebrow">Win Points</p>
              <Gift size={14} className="text-[var(--twr-bronze)]" />
            </div>
            <p className="font-serif text-5xl text-[var(--twr-bronze)] mt-4" data-testid="member-points-balance">
              {points ? points.balance.toLocaleString() : "—"}
            </p>
            <p className="text-[var(--twr-warm-white)]/45 text-[12px] mt-2">Earn 1 point per $1 spent in the shop, bar & restaurant.</p>
          </div>

          <div className="surface p-7" data-testid="member-visits">
            <div className="flex items-center justify-between">
              <p className="eyebrow">Recent Visits</p>
              <Calendar size={14} className="text-[var(--twr-bronze)]" />
            </div>
            {visits.length === 0 ? (
              <p className="text-[var(--twr-warm-white)]/55 text-sm mt-5">No visits logged yet. Your next check-in will appear here.</p>
            ) : (
              <ul className="mt-5 space-y-3">
                {visits.slice(0, 8).map((v) => (
                  <li key={v.id} className="flex items-center justify-between text-sm">
                    <span className="text-[var(--twr-warm-white)]/85">
                      {new Date(v.checked_in_at).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "2-digit" })}
                    </span>
                    <span className="text-[var(--twr-warm-white)]/55 text-[12px]">
                      {new Date(v.checked_in_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="surface p-7">
            <p className="eyebrow">Quick Actions</p>
            <div className="mt-5 grid gap-2">
              <Link to="/reservations" className="btn-ghost justify-start">Reserve a Table</Link>
              <Link to="/events" className="btn-ghost justify-start">Browse Events</Link>
              <Link to="/private-spaces" className="btn-ghost justify-start">Book a Private Space</Link>
            </div>
          </div>
        </aside>
      </div>

      <div className="mt-14">
        <RewardsSection points={points} rewards={rewards} redemptions={redemptions} onChanged={loadLoyalty} />
      </div>
    </div>
  );
}

function RewardsSection({ points, rewards, redemptions, onChanged }) {
  const balance = points?.balance ?? 0;
  const [busyId, setBusyId] = useState(null);

  const redeem = async (r) => {
    if (!window.confirm(`Redeem "${r.name}" for ${r.points_cost.toLocaleString()} points?`)) return;
    setBusyId(r.id);
    try {
      const res = await memberApi.post("/member/redeem", { reward_id: r.id });
      toast.success(`Redeemed — show this to staff on your next visit. Balance: ${res.data.balance.toLocaleString()} pts`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not redeem");
    } finally { setBusyId(null); }
  };

  return (
    <section data-testid="member-rewards-section">
      <p className="eyebrow">Rewards Catalog</p>
      <h2 className="font-serif text-3xl sm:text-4xl mt-3">Spend your points.</h2>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rewards.map((r) => {
          const affordable = balance >= r.points_cost;
          return (
            <div key={r.id} data-testid={`member-reward-${r.id}`} className={`surface p-6 flex flex-col ${!affordable ? "opacity-60" : ""}`}>
              <p className="text-[11px] tracking-[0.22em] uppercase text-[var(--twr-bronze)]">{r.category}</p>
              <p className="font-serif text-xl mt-2">{r.name}</p>
              <p className="text-[var(--twr-warm-white)]/55 text-[13px] mt-2 flex-1">{r.description}</p>
              <div className="mt-5 flex items-center justify-between gap-3">
                <p className="font-serif text-2xl text-[var(--twr-bronze)]">{r.points_cost.toLocaleString()} <span className="text-sm">pts</span></p>
                <button
                  onClick={() => redeem(r)}
                  disabled={!affordable || busyId === r.id}
                  data-testid={`member-redeem-${r.id}`}
                  className={`px-4 py-2 text-[11px] tracking-[0.28em] uppercase border transition-colors ${affordable ? "border-[var(--twr-bronze)] text-[var(--twr-bronze)] hover:bg-[var(--twr-bronze)] hover:text-[#0A0A0A]" : "border-[var(--twr-line-strong)] text-[var(--twr-warm-white)]/35 cursor-not-allowed"}`}>
                  {busyId === r.id ? "Redeeming…" : affordable ? "Redeem" : `${(r.points_cost - balance).toLocaleString()} more`}
                </button>
              </div>
            </div>
          );
        })}
        {rewards.length === 0 && <p className="text-[var(--twr-warm-white)]/55 col-span-full py-8">Rewards are coming soon.</p>}
      </div>

      <div className="mt-12 grid lg:grid-cols-2 gap-8">
        <div className="surface p-7" data-testid="member-redemptions">
          <p className="eyebrow">My Redemptions</p>
          {redemptions.length === 0 ? (
            <p className="text-[var(--twr-warm-white)]/55 text-sm mt-5">Nothing redeemed yet.</p>
          ) : (
            <ul className="mt-5 space-y-3">
              {redemptions.slice(0, 8).map((d) => (
                <li key={d.id} className="flex items-center justify-between text-sm gap-3">
                  <span className="text-[var(--twr-warm-white)]/85 truncate">{d.reward_name}</span>
                  <span className={`text-[11px] tracking-[0.18em] uppercase shrink-0 ${d.status === "pending" ? "text-amber-400" : d.status === "fulfilled" ? "text-emerald-400" : "text-[var(--twr-warm-white)]/45"}`}>{d.status}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[var(--twr-warm-white)]/45 text-[12px] mt-5">Pending redemptions are honoured by staff on your next visit.</p>
        </div>

        <div className="surface p-7" data-testid="member-points-history">
          <p className="eyebrow">Points History</p>
          {!points || points.transactions.length === 0 ? (
            <p className="text-[var(--twr-warm-white)]/55 text-sm mt-5">Your points activity will appear here.</p>
          ) : (
            <ul className="mt-5 space-y-3">
              {points.transactions.slice(0, 8).map((t) => (
                <li key={t.id} className="flex items-center justify-between text-sm gap-3">
                  <span className="text-[var(--twr-warm-white)]/75 truncate">{t.description}</span>
                  <span className={`font-serif shrink-0 ${t.points >= 0 ? "text-emerald-400" : "text-red-400"}`}>{t.points >= 0 ? "+" : ""}{t.points.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[11px] tracking-[0.22em] uppercase text-[var(--twr-warm-white)]/55">{label}</span>
      <span className="text-[var(--twr-warm-white)] text-right truncate">{value}</span>
    </div>
  );
}
